import React, { useEffect, useReducer, useCallback } from "react";
import { ActivityIndicator, View, Text } from "react-native";
import Dropdown from "./Dropdown";
import { getSession } from "../utils/cognitoAuth";

const initialState = {
    universityData: [],
    selectedUniversity: "",
    loading: true,
    error: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case "SET_DATA":
            return { ...state, universityData: action.payload, loading: false, error: false };
        case "SET_SELECTED":
            return { ...state, selectedUniversity: action.payload };
        case "SET_LOADING":
            return { ...state, loading: true, error: false };
        case "SET_ERROR":
            return { ...state, loading: false, error: true };
        default:
            return state;
    }
};

const useUniversityData = () => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const fetchUniversityData = useCallback(async () => {
        dispatch({ type: "SET_LOADING" });

        try {
            const session = await getSession();
            if (!session) throw new Error("Session not available");

            const token = session.accessToken;
            const response = await fetch("https://vault-api.umo.services/data/universities", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error("Failed to fetch universities");

            const data = await response.json();
            const formattedData = data.map((item) => ({
                label: item.label,
                value: item.value,
            }));

            dispatch({ type: "SET_DATA", payload: formattedData });
        } catch (error) {
            dispatch({ type: "SET_ERROR" });
            console.error("Error fetching university data:", error);
        }
    }, []);

    useEffect(() => {
        fetchUniversityData();
    }, [fetchUniversityData]);

    return [state, dispatch];
};

const UniversityDropdown = ({ onSelect, selectedUniversity: propSelectedUniversity }) => {
    const [{ universityData, selectedUniversity, loading, error }, dispatch] = useUniversityData();

    // Sync propSelectedUniversity to internal state once universityData is ready
    useEffect(() => {
        if (
          universityData.length > 0 &&
          propSelectedUniversity &&
          propSelectedUniversity !== selectedUniversity
        ) {
            dispatch({ type: "SET_SELECTED", payload: propSelectedUniversity });
        }
    }, [universityData, propSelectedUniversity, selectedUniversity]);

    const handleSelect = (selectedItem, index) => {
        const selectedValue = universityData[index].value;
        dispatch({ type: "SET_SELECTED", payload: selectedValue });

        if (onSelect) {
            onSelect(selectedItem, index);
        }
    };

    if (loading) {
        return <ActivityIndicator size="large" color="#0000ff" />;
    }

    return (
      <View>
          {error ? (
            <Text style={{ color: "red", textAlign: "center" }}>
                Error fetching universities
            </Text>
          ) : (
            <Dropdown
              value={selectedUniversity}
              data={universityData}
              onSelect={handleSelect}
              buttonTextAfterSelection={(selectedItem) => selectedItem.label}
              rowTextForSelection={(item) => item.label}
              simple
              label="Institution"
              search
              defaultButtonText="Select an Institution"
            />
          )}
      </View>
    );
};

export default UniversityDropdown;